import React from 'react';
import { Row, Col, FormGroup, Label, Input, FormFeedback, Card, CardBody } from 'reactstrap';
import { GENDER_OPTIONS } from '../../../../../constants/gender';
import { MARITAL_STATUS_OPTIONS } from '../../../../../constants/users';

const ProfileMyData = ({ isEditing, formik }) => {

    const { values, errors, touched, getFieldProps } = formik;

    /**
     * Acessa valor em path com notação de ponto (ex: "emergencyContact.name")
     */
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    /**
     * renderField — renderiza input sempre visível.
     * Em modo view: input disabled com estilo limpo.
     * Em modo edit: input habilitado para edição.
     */
    const renderField = (name, label, type = "text", placeholder = "", options = []) => {
        const hasError = isEditing && getNestedValue(touched, name) && !!getNestedValue(errors, name);
        const fieldValue = getNestedValue(values, name);

        // Para modo visualização de selects, mostramos o label correspondente
        if (!isEditing && type === "select" && options.length > 0) {
            const opt = options.find(o => o.value === fieldValue);
            const displayLabel = opt ? opt.label : (fieldValue || "");

            return (
                <FormGroup className="mb-3">
                    <Label className="text-muted small text-uppercase fw-bold mb-1">{label}</Label>
                    <Input
                        type="text"
                        value={displayLabel}
                        disabled
                        className="border-0 bg-transparent px-0 py-1 shadow-none"
                        style={{ opacity: 1, color: '#495057' }}
                    />
                </FormGroup>
            );
        }

        // Para datas em modo view, formata DD/MM/YYYY
        if (!isEditing && name === "birthDate" && fieldValue && typeof fieldValue === 'string') {
            let formatted = fieldValue;
            try {
                const [year, month, day] = fieldValue.split('-');
                if (year && month && day) formatted = `${day}/${month}/${year}`;
            } catch(e) { /* mantém original */ }

            return (
                <FormGroup className="mb-3">
                    <Label className="text-muted small text-uppercase fw-bold mb-1">{label}</Label>
                    <Input
                        type="text"
                        value={formatted}
                        disabled
                        className="border-0 bg-transparent px-0 py-1 shadow-none"
                        style={{ opacity: 1, color: '#495057' }}
                    />
                </FormGroup>
            );
        }

        return (
            <FormGroup className="mb-3">
                <Label className="text-muted small text-uppercase fw-bold mb-1">{label}</Label>
                {type === "select" ? (
                    <Input
                        type="select"
                        name={name}
                        className={isEditing ? "bg-light border-0 py-2" : "border-0 bg-transparent px-0 py-1 shadow-none"}
                        {...getFieldProps(name)}
                        value={fieldValue || ""}
                        disabled={!isEditing}
                        invalid={hasError}
                        style={!isEditing ? { opacity: 1, color: '#495057', appearance: 'none' } : {}}
                    >
                        <option value="">Selecione...</option>
                        {options.map((opt, idx) => (
                            <option key={idx} value={opt.value}>{opt.label}</option>
                        ))}
                    </Input>
                ) : (
                    <Input
                        type={type}
                        name={name}
                        placeholder={isEditing ? placeholder : ""}
                        className={isEditing ? "bg-light border-0 py-2" : "border-0 bg-transparent px-0 py-1 shadow-none"}
                        {...getFieldProps(name)}
                        value={fieldValue || ""}
                        disabled={!isEditing}
                        invalid={hasError}
                        style={!isEditing ? { opacity: 1, color: '#495057' } : {}}
                    />
                )}
                {hasError && <FormFeedback>{getNestedValue(errors, name)}</FormFeedback>}
            </FormGroup>
        );
    };

    return (
        <Card className="border-0 shadow-sm rounded-0 mb-0">
            <CardBody className="p-4">

                {/* ── DADOS PESSOAIS ── */}
                <div className="d-flex align-items-center mb-4">
                    <span className="bg-primary-soft text-primary p-2 rounded-0 me-2 d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                        <i className="mdi mdi-account-circle-outline font-size-18"></i>
                    </span>
                    <h6 className="text-uppercase font-size-13 fw-bold text-primary mb-0">Dados Pessoais</h6>
                </div>
                <Row>
                    <Col md={4}>{renderField("firstName", "Nome", "text", "Ex: João")}</Col>
                    <Col md={8}>{renderField("lastName", "Sobrenome", "text", "Ex: Silva")}</Col>
                    <Col md={6}>{renderField("email", "E-mail", "email", "Ex: joao@email.com")}</Col>
                    <Col md={6}>{renderField("phone", "Telefone", "text", "Ex: (11) 99999-9999")}</Col>
                    <Col md={4}>{renderField("document", "Documento (CPF)", "text", "Ex: 000.000.000-00")}</Col>
                    <Col md={3}>{renderField("birthDate", "Data de Nascimento", "date")}</Col>
                    <Col md={2}>{renderField("gender", "Sexo", "select", "", GENDER_OPTIONS)}</Col>
                    <Col md={3}>{renderField("maritalStatus", "Estado Civil", "select", "", MARITAL_STATUS_OPTIONS)}</Col>
                </Row>

                <hr className="my-4 border-dashed" />

                {/* ── ENDEREÇO ── */}
                <div className="d-flex align-items-center mb-4">
                    <span className="bg-primary-soft text-primary p-2 rounded-0 me-2 d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                        <i className="mdi mdi-map-marker-outline font-size-18"></i>
                    </span>
                    <h6 className="text-uppercase font-size-13 fw-bold text-primary mb-0">Endereço</h6>
                </div>
                <Row>
                    <Col md={3}>{renderField("zipCode", "CEP", "text", "Ex: 00000-000")}</Col>
                    <Col md={7}>{renderField("address", "Logradouro", "text", "Ex: Rua das Flores")}</Col>
                    <Col md={2}>{renderField("number", "Número", "text", "Ex: 123")}</Col>
                    <Col md={4}>{renderField("complement", "Complemento", "text", "Ex: Apto 101")}</Col>
                    <Col md={4}>{renderField("neighborhood", "Bairro", "text", "Ex: Centro")}</Col>
                    <Col md={3}>{renderField("city", "Cidade", "text", "Ex: São Paulo")}</Col>
                    <Col md={1}>{renderField("state", "UF", "text", "SP")}</Col>
                </Row>

                <hr className="my-4 border-dashed" />

                {/* ── CONTATO DE EMERGÊNCIA ── */}
                <div className="d-flex align-items-center mb-4">
                    <span className="bg-primary-soft text-primary p-2 rounded-0 me-2 d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                        <i className="mdi mdi-phone-alert-outline font-size-18"></i>
                    </span>
                    <h6 className="text-uppercase font-size-13 fw-bold text-primary mb-0">Contato de Emergência</h6>
                </div>
                <Row>
                    <Col md={4}>{renderField("emergencyContact.name", "Nome do Contato", "text", "Nome do parente ou amigo")}</Col>
                    <Col md={3}>{renderField("emergencyContact.phone", "Telefone", "text", "(11) 99999-9999")}</Col>
                    <Col md={3}>{renderField("emergencyContact.email", "E-mail", "email", "email@exemplo.com")}</Col>
                    <Col md={2}>{renderField("emergencyContact.relationship", "Parentesco", "text", "Ex: Mãe, Pai")}</Col>
                </Row>

                <hr className="my-4 border-dashed" />

                {/* ── INFORMAÇÕES MÉDICAS ── */}
                <div className="d-flex align-items-center mb-4">
                    <span className="bg-primary-soft text-primary p-2 rounded-0 me-2 d-inline-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                        <i className="mdi mdi-heart-pulse font-size-18"></i>
                    </span>
                    <h6 className="text-uppercase font-size-13 fw-bold text-primary mb-0">Saúde</h6>
                </div>
                <Row>
                    <Col md={4}>{renderField("healthInfo.bloodType", "Tipo Sanguíneo", "text", "Ex: O+")}</Col>
                    <Col md={8}>{renderField("healthInfo.allergies", "Alergias", "text", "Possui alguma alergia?")}</Col>
                    <Col md={6}>{renderField("healthInfo.medications", "Uso de Medicamentos", "text", "Toma algum medicamento?")}</Col>
                    <Col md={6}>{renderField("healthInfo.medicalConditions", "Condições Médicas", "text", "Ex: Hipertensão, Asma")}</Col>
                    <Col md={12}>{renderField("healthInfo.observations", "Observações Gerais", "textarea", "Outras informações relevantes")}</Col>
                </Row>

            </CardBody>
        </Card>
    );
};

export default ProfileMyData;
